'use client';

import type { NextPage } from 'next';
import Image from 'next/image';
import { useForm, Controller } from 'react-hook-form';
import React from 'react';

import styles from './items-form-block1440px.module.scss';
import ButtonSimple from '@/components/HeroBlock/components/ButtonSimple/ButtonSimple';
import TextField from './components/Text-field/Text-field';
import {
  ItemsFormBlock1440pxType,
  RaceTeamFormData,
} from '@/types/itemsFormBlock.types';
import { ErrorModal, SuccessModal } from '@/components';
import { useState, useRef } from 'react';
import CheckboxForForm from '../CheckboxForForm/CheckboxForForm';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

const ItemsFormBlockRaceTeam: NextPage<ItemsFormBlock1440pxType> = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<RaceTeamFormData>({
    defaultValues: {
      email: '',
      name: '',
      message: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: RaceTeamFormData) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/race-team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, ...attribution }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      reset();
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <>
      <section className={styles.wrapper}>
        <div className="container">
          <div className={`${styles.formblock}`}>
            <div className={styles.leftcol}>
              <Image
                src="/img/race-team/car-image.webp"
                alt="Race Team"
                width={638}
                height={514}
                className={styles.raceTeamImage}
              />
            </div>
            <div className={styles.rightcol}>
              <h3 className={styles.interestedInSponsorship}>
                Interested in Sponsorship or Partnership Information?
              </h3>
              <form
                ref={formRef}
                onSubmit={handleSubmit(onSubmit)}
                className={styles.form}
              >
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: 'Name is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Your name"
                      placeholder="Enter your name"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Your email"
                      placeholder="Enter your email address"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(filterCyrillicFromEmail(e.target.value))
                      }
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  name="message"
                  control={control}
                  rules={{
                    required: 'Message is required',
                    minLength: {
                      value: 4,
                      message: 'Message should be at least 4 characters',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={styles.textField}>
                      <div className={styles.label}>Your message</div>
                      <textarea
                        className={`${styles.row} ${
                          fieldState.error ? styles.error : ''
                        }`}
                        placeholder="Enter your message (any questions or wishes)"
                        rows={4}
                        cols={32}
                        {...field}
                      />
                      {fieldState.error && (
                        <div className={styles.errorText}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
                <CheckboxForForm
                  consentChecked={consentChecked}
                  setConsentChecked={setConsentChecked}
                />

                <div className={styles.buttonWrapper}>
                  <ButtonSimple
                    text={isSubmitting ? 'Sending...' : 'Submit'}
                    designBlendingMode=""
                    designBlendingMode1=""
                    type="submit"
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </>
  );
};

export default ItemsFormBlockRaceTeam;
